import {Component, Input, Optional, Self} from '@angular/core';
import {ControlValueAccessor, NgControl} from '@angular/forms';

@Component({
    selector: 'employee-request-input',
    templateUrl: './request-input.component.html',
    styleUrls: ['./request-input.component.less'],
})
export class RequestInputComponent implements ControlValueAccessor {

    @Input() title: string;
    @Input() placeholder: string = 'Enter';
    @Input() isRequired = true;
    @Input() type: 'text' | 'date' | 'datetime-local' | 'email' = 'text';
    @Input() notFeature = false;
    @Input() mask: string = '';
    @Input() minValue: string | 'today';
    @Input() maxDate: Date;
    @Input() prefix: string = '';
    @Input() maxLength = 200;
    @Input() infoText = '';
    @Input() disabled = false;
    @Input() noOfDays: number;
    @Input() maskPhoneErrText: string = 'Invalid phone format';
    @Input() maskEmailErrText: string = 'Invalid email format';
    @Input() requiredErrText: string = 'Required';
  
    value: string = '';
    touched = false;

    internalPrefix = '';
    internalMask = '';
    errors = [];
    
    constructor(@Optional() @Self() public control: NgControl) {
        if (control) {
            control.valueAccessor = this;
        }
    }

    private formatDateLocal(value: Date): string {
        const year = value.getFullYear();
        const month = String(value.getMonth() + 1).padStart(2, '0');
        const day = String(value.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    }

    private formatDateTimeLocal(value: Date): string {
        const date = this.formatDateLocal(value);
        const hours = String(value.getHours()).padStart(2, '0');
        const minutes = String(value.getMinutes()).padStart(2, '0');
        const seconds = String(value.getSeconds()).padStart(2, '0');
        return `${date}T${hours}:${minutes}:${seconds}`;
    }

    /**
     * Parse a min-value string as LOCAL midnight to avoid UTC-shift issues.
     * ISO date-only strings ("yyyy-MM-dd") use the 3-arg Date constructor so
     * the result is local midnight, not UTC midnight.
     */
    private parseMinDate(value: string): Date {
        if (value === 'today') {
            return new Date();
        }
        if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
            const [y, m, d] = value.split('-').map(Number);
            return new Date(y, m - 1, d);
        }
        return new Date(value);
    }
   
    get _minValue(): string {
        if (!this.minValue) {
            return null;
        }

        if (this.type === 'text') {
            return this.minValue;
        } else {
            const minDate = this.parseMinDate(this.minValue);
            return (this.type === 'date')
                ? this.formatDateLocal(minDate)
                : this.formatDateTimeLocal(minDate);
        }
    }

    get maxValue(): any {
        if (this.minValue && this.noOfDays) {
            const minDate = this.parseMinDate(this.minValue);
            const maxDate = new Date(minDate);
            maxDate.setDate(minDate.getDate() + this.noOfDays);
            return this.type === 'date'
                ? this.formatDateLocal(maxDate)
                : this.formatDateTimeLocal(maxDate);
        }
        return (this.type === 'datetime-local' && this.minValue)
             ? '2055-02-15T00:00'
             : null;
    }

    get isEmail(): boolean {
        return this.type === 'email';
    }

    get showStaticPrefix(): boolean {
        return !!this.prefix && !this.mask;
    }

    get displayPlaceholder(): string {
        if (!this.showStaticPrefix || !this.placeholder) {
            return this.placeholder;
        }

        return this.placeholder.startsWith(this.prefix)
            ? this.placeholder.slice(this.prefix.length)
            : this.placeholder;
    }
  
    writeValue(value: string): void {
        this.value = value;
        if (this.prefix || this.mask) {
            this.maskUpdate(value || '');
        }
    }

    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouch = fn;
    }

    setDisabledState?(isDisabled: boolean): void {
    }

    maskUpdate(value: string): void {
        this.internalPrefix = this.prefix && (!this.mask || !!value) ? this.prefix : '';
        this.internalMask = value ? this.mask : '';
    }

    inputHandler($event: Event): void {
        const inputElement = $event.target as HTMLInputElement;
        let inputValue = inputElement.value;

        if (this.showStaticPrefix) {
            inputValue = inputValue.replace(/\D/g, '');
            inputElement.value = inputValue;
            this.value = inputValue;
        }

        if (this.prefix || this.mask) {
            this.maskUpdate(inputValue);
        }
        this.touched = true;

        this.onChange(inputValue);
        this.onTouch();
    }

    private onChange: any = () => {
    }

    private onTouch: any = () => {
    }
}
